import type { CSSProperties, ElementType, FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'

interface RevealProps {
    as?: ElementType
    children?: ReactNode
    className?: string
    delay?: number
    id?: string
    style?: CSSProperties
}

let sharedObserver: IntersectionObserver | null = null

const getObserver = (): IntersectionObserver => {
    if (sharedObserver) return sharedObserver

    sharedObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
            if (!entry.isIntersecting) continue

            const target = entry.target as HTMLElement

            target.dataset.reveal = 'shown'
            sharedObserver?.unobserve(target)
        }
    }, {
        threshold: 0.12,
        rootMargin: '0px 0px -6% 0px'
    })

    return sharedObserver
}

/** Fades its content up once, the first time it scrolls into view. */
export const Reveal: FC<RevealProps> = ({
    as: Tag = 'div',
    children,
    className,
    delay = 0,
    id,
    style
}): ReactNode => {
    const ref = useRef<HTMLElement>(null)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        if (typeof IntersectionObserver === 'undefined') {
            element.dataset.reveal = 'shown'
            return
        }

        const observer = getObserver()

        observer.observe(element)

        return () => observer.unobserve(element)
    }, [])

    return (
        <Tag
            ref={ref}
            id={id}
            data-reveal=''
            className={className}
            style={{ ...style, '--reveal-delay': `${delay}ms` } as CSSProperties}
        >
            {children}
        </Tag>
    )
}
